import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMapPin, FiPhone, FiClock, FiAward, FiCalendar } from 'react-icons/fi';
import { SITE_CONFIG } from '../data/content';

const Footer = () => {
  return (
    <footer className="bg-[#0a1e1e] text-teal-50/80 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 bg-teal-900/30 p-8 rounded-[2.5rem] border border-teal-800/50">
          <div className="flex items-center gap-6">
            <div className="bg-teal-600 p-4 rounded-2xl hidden md:block">
              <SafeIcon icon={FiPhone} className="text-white text-2xl" />
            </div>
            <div>
              <p className="text-teal-400 font-black text-sm uppercase tracking-widest mb-1">Immediate Assistance</p>
              <a href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`} className="text-2xl md:text-3xl font-black text-white hover:text-teal-300 transition-colors">
                {SITE_CONFIG.phone}
              </a>
            </div>
          </div>
          <div className="flex items-center lg:justify-end">
            <a href={SITE_CONFIG.bookingUrl} target="_blank" className="bg-white text-teal-900 px-8 py-4 rounded-2xl font-black flex items-center gap-3 hover:bg-teal-50 transition-all w-full lg:w-auto justify-center">
              <SafeIcon icon={FiCalendar} /> Book Appointment Online
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="space-y-6">
            <Link to="/" className="block group">
              <div className="bg-white p-4 rounded-2xl inline-block mb-4 transition-transform group-hover:scale-105">
                <img src={SITE_CONFIG.logo} alt={SITE_CONFIG.chiropracticBrand} className="h-12 w-auto" />
              </div>
              <span className="text-white font-bold text-xl block leading-tight">
                Tuggeranong<br />Chiropractic Centre
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-teal-100/60">
              {SITE_CONFIG.anniversary}. We focus on evidence-based management to restore your movement and wellbeing.
            </p>
            <div className="flex items-center gap-2 text-teal-400 font-bold text-xs uppercase tracking-widest">
              <SafeIcon icon={FiAward} />
              <span>38,000+ patients served</span>
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold text-lg mb-6 border-b border-teal-800 pb-2 inline-block">Chiropractic care</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="hover:text-teal-300 transition-colors">Home</Link></li>
              <li><Link to="/back-pain" className="hover:text-teal-300 transition-colors">Back pain relief</Link></li>
              <li><Link to="/neck-pain" className="hover:text-teal-300 transition-colors">Neck pain care</Link></li>
              <li><Link to="/headaches-migraines" className="hover:text-teal-300 transition-colors">Headaches & migraines</Link></li>
              <li><Link to="/x-rays" className="hover:text-teal-300 transition-colors">On-site X-rays</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold text-lg mb-6 border-b border-teal-800 pb-2 inline-block">Massage & more</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/massage" className="hover:text-teal-300 transition-colors">Remedial massage</Link></li>
              <li><Link to="/lymphatic-drainage" className="hover:text-teal-300 transition-colors">Lymphatic drainage</Link></li>
              <li><Link to="/myofascial-dry-needling" className="hover:text-teal-300 transition-colors">Dry needling</Link></li>
              <li><Link to="/custom-orthotics" className="hover:text-teal-300 transition-colors">Custom orthotics</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold text-lg mb-6 border-b border-teal-800 pb-2 inline-block">Contact & location</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 group">
                <SafeIcon icon={FiMapPin} className="text-teal-400 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-white transition-colors">{SITE_CONFIG.address}</span>
              </li>
              <li className="flex items-center gap-3 group">
                <SafeIcon icon={FiPhone} className="text-teal-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="font-bold text-white group-hover:text-teal-300 transition-colors">{SITE_CONFIG.phone}</span>
              </li>
              <li className="flex items-start gap-3 group">
                <SafeIcon icon={FiClock} className="text-teal-400 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <div className="group-hover:text-white transition-colors">
                  <p>Mon - Fri: 8am - 6pm</p>
                  <p>Sat: 9am - 1pm</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-teal-900/50 pt-8 pb-12">
          <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-4 opacity-60">Areas we serve</h4>
          <p className="text-[11px] leading-relaxed text-teal-100/40">
            Centrally located in Fadden, we provide professional care to patients from <span className="text-teal-100/60">{SITE_CONFIG.nearbySuburbs.join(', ')}</span> and across the greater Canberra region.
          </p>
        </div>

        <div className="border-t border-teal-900 pt-10 flex flex-col md:flex-row justify-between items-center text-xs text-teal-100/40">
          <p>&copy; {new Date().getFullYear()} {SITE_CONFIG.chiropracticBrand}. All rights reserved.</p>
          <div className="flex space-x-6 mt-6 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy policy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;